import { useState, type ChangeEvent } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { PermissionGate } from '../PermissionGate';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { saveCredential, VIA_SFTP } from './credentialsApi';

/**
 * Login i hasło serwera SFTP, na który idzie plik zlecenia do importu TMS
 * (15.10.2026).
 *
 * **Tylko zapis.** Pola nigdy nie pokazują zapisanej wartości — magazyn jej
 * nie oddaje, a w tabeli niżej stoi maska. Puste pole znaczy „bez zmian”,
 * więc zmiana samego hasła nie wymaga przepisywania loginu.
 *
 * Poświadczenie wspólne dla instancji (bez spółki): serwer importu jest
 * jeden dla obu spółek, spółkę niesie treść pliku. Adres, port, katalog
 * (`preprod` / `prod`) i odcisk klucza serwera to konfiguracja wdrożenia
 * (env), nie ten ekran. O dostępie rozstrzyga API (zarządzanie kontami).
 */
export const ViaSftpCredentialsForm = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      if (login.trim() !== '') {
        await saveCredential({
          system: VIA_SFTP.SYSTEM,
          field: VIA_SFTP.LOGIN,
          value: login.trim(),
          companyId: null,
          description: 'Serwer SFTP importu TMS — login',
        });
      }
      if (password !== '') {
        await saveCredential({
          system: VIA_SFTP.SYSTEM,
          field: VIA_SFTP.PASSWORD,
          value: password,
          companyId: null,
          description: 'Serwer SFTP importu TMS — hasło',
        });
      }
      setLogin('');
      setPassword('');
      notify('Zapisano w magazynie poświadczeń — wartości nie da się tu odczytać', {
        type: 'success',
      });
      refresh();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <PermissionGate permission="MANAGE_USERS">
      <Box data-testid="via-sftp-credentials" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Serwer SFTP importu TMS (wspólny dla obu spółek):
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            label="Nazwa użytkownika"
            value={login}
            autoComplete="off"
            onChange={(e: ChangeEvent<HTMLInputElement>) => setLogin(e.target.value)}
          />
          <TextField
            size="small"
            label="Hasło"
            type="password"
            value={password}
            autoComplete="new-password"
            onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={busy || (login.trim() === '' && password === '')}
            onClick={() => void save()}
          >
            Zapisz w magazynie
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Pola nie pokazują zapisanych wartości; puste pole zostawia dotychczasową. Adres serwera,
          katalog (preprod / prod) i odcisk klucza ustawia konfiguracja wdrożenia.
        </Typography>
      </Box>
    </PermissionGate>
  );
};
